import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"
import { Chevron } from "@components/icons"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { body14Styles, h5Styles } from "@constants/typography"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import loadable from "@loadable/component"
import { withFinePrint } from "@utils/ContentfulRichText"
import { getIn } from "@utils/index"
import { emptyRichText } from "@utils/richText"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import React from "react"
import styled, { css, keyframes } from "styled-components"

const Slider = loadable(() => import("react-slick"), { ssr: false })

import Button, {
  StyledButton as ButtonContainer,
  Text as ButtonText,
} from "../buttons/Button"
import LinkButton, {
  Button as LinkButtonContainer,
  TextWrapper as LinkButtonText,
} from "../buttons/LinkButton"
import { PromoBannerProps } from "./common"

const baseButtonStyles = css`
  border: none;
  background-color: transparent;
  outline: none;

  &:focus {
    outline: none;
  }

  &:active {
    outline: none;
  }
`

const Container = styled.div`
  padding: 8px 0;
  background-color: ${colors.offBlack};

  @media ${breakpoints.mediumLaptop} {
    display: none;
  }
`

const ExpandedContent = styled.div<{ expanded: boolean }>`
  ${({ expanded }) => css`
    z-index: 2;
    position: absolute;
    top: 0;
    left: 0;
    transform: translateY(${expanded ? "0" : "-100%"});
    transition: transform ${animation.medium} ${cubicBezier};
    width: 100%;
    padding: 68px 20px 0;
    border-bottom: solid 1px ${colors.dividerLineGray};
    background-color: ${colors.offWhite};
  `}
`

const ExpandedHeader = styled.div`
  ${h5Styles}
  padding-right: 40px;
`

const ExpandedBody = styled.div`
  ${body14Styles};
  margin: 30px 0 36px;
  padding-right: 20px;
  color: ${colors.mediumGray};

  p {
    margin: 0 0 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul {
    margin: 0 0 20px;
    padding-left: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const Link = styled.a``

const ActionButton = styled(Button)`
  ${ButtonContainer} {
    width: initial;
  }

  ${ButtonText} {
    padding-right: 36px;
  }
`

const ActionLink = styled(LinkButton)`
  ${LinkButtonContainer} {
    width: initial;
  }

  ${LinkButtonText} {
    padding-right: 36px;
  }
`

const CollapseButton = styled.button`
  ${baseButtonStyles}
  align-items: center;
  justify-content: center;
  display: flex;
  width: 100%;
  height: 57px;
  margin-top: 32px;
`

const CollapseIconWrapper = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  transform: rotate(-90deg);
  width: 36px;
  height: 36px;
`

const PromoWrapper = styled.div`
  padding: 0 8px;
`

const Promo = styled.button<{ extended: boolean }>`
  ${({ extended }) => css`
    ${baseButtonStyles}
    align-items: center;
    display: flex;
    width: ${extended ? "70vw" : "50vw"} !important;
    height: 32px;
    padding: 0 16px 0 18px;
    border-radius: 2px;
    background-color: ${colors.black30};
  `}
`

const PromoHeader = styled.div`
  ${body14Styles}
  overflow: hidden;
  width: 100%;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${colors.offWhite};
`

const PromoIconWrapper = styled.div`
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  display: flex;
  transform: rotate(90deg);
  width: 24px;
  height: 24px;
  margin-left: 2px;
`

const fadeIn = keyframes`
  0% {
    background-color: rgb(0, 0, 0, 0);
  }
  
  100% {
    background-color: rgb(0, 0, 0, 0.5);
  }
`

const SliderContainer = styled.div`
  .slick-track {
    height: 32px;
    overflow: hidden;
    margin-left: 13.33vw;
  }
`

const BackgroundOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  background-color: rgb(0, 0, 0, 0.5);
  animation: ${fadeIn} ease 0.5s;
`

const MobilePromoBanner: React.FC<PromoBannerProps> = ({
  promos,
  promoScrollDelay,
}) => {
  const [swiped, setSwiped] = React.useState(false)

  const handleSwiped = React.useCallback(() => {
    setSwiped(true)
  }, [setSwiped])

  const [expanded, setExpanded] = React.useState<boolean>(false)

  const expandBody = () => {
    setExpanded(true)
  }

  const collapseBody = () => {
    setExpanded(false)
  }

  const [promoIndex, setPromoIndex] = React.useState<number>(0)

  const promosWithHandlers = React.useMemo(
    () =>
      promos.map((promo, i) => ({
        ...promo,
        onClick: (e) => {
          if (swiped) {
            e.stopPropagation()
            e.preventDefault()
            setSwiped(false)
          } else {
            e.stopPropagation()
            setPromoIndex(i)
            expandBody()
          }
        },
      })),
    [promos, swiped]
  )

  const expandedContainerRef: any = React.useRef(null)

  useModalBackgroundClose({
    isOpen: expanded,
    modalRef: expandedContainerRef,
    onRequestClose: collapseBody,
  })

  const expandedSliderRef = React.useRef<any>(null)

  const [activeSlide, setActiveSlide] = React.useState<number>(0)

  const onNextSlide = () => {
    expandedSliderRef?.current?.slickNext()
  }

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      if (!expanded) {
        onNextSlide()
      }
    }, parseInt(promoScrollDelay))

    return () => {
      clearTimeout(timeout)
    }
  }, [activeSlide, expanded, promoScrollDelay])

  if (!promosWithHandlers || promosWithHandlers.length === 0) {
    return null
  }

  return (
    <Container>
      {expanded && <BackgroundOverlay />}
      <ExpandedContent expanded={expanded} ref={expandedContainerRef}>
        <ExpandedHeader>{promosWithHandlers[promoIndex].header}</ExpandedHeader>
        <ExpandedBody>
          {renderRichText(
            getIn(promosWithHandlers[promoIndex], "body", emptyRichText),
            withFinePrint
          )}
        </ExpandedBody>
        {promosWithHandlers[promoIndex].link && (
          <ActionLink
            text={promosWithHandlers[promoIndex].buttonText || "Go"}
            to={promosWithHandlers[promoIndex].link!}
          />
        )}
        {promosWithHandlers[promoIndex].externalLink && (
          <Link
            href={promosWithHandlers[promoIndex].externalLink}
            rel="noopener"
            target="_blank"
          >
            <ActionButton
              text={promosWithHandlers[promoIndex].buttonText || "Go"}
            />
          </Link>
        )}
        <CollapseButton onClick={collapseBody}>
          <CollapseIconWrapper>
            <Chevron strokeColor={colors.mediumDarkGray} />
          </CollapseIconWrapper>
        </CollapseButton>
      </ExpandedContent>
      <SliderContainer>
        <Slider
          afterChange={() => setSwiped(false)}
          arrows={false}
          autoplay={false}
          beforeChange={(_, next) => setActiveSlide(next)}
          centerMode
          dots={false}
          infinite
          onSwipe={handleSwiped}
          ref={expandedSliderRef}
          swipeToSlide
          variableWidth
        >
          {promosWithHandlers.map(({ header, onClick }, i) => (
            <PromoWrapper
              hidden={activeSlide === i ? true : undefined}
              key={`${i}-${header}`}
              onClickCapture={onClick}
            >
              <Promo extended={promosWithHandlers.length === 1}>
                <PromoHeader>{header}</PromoHeader>
                <PromoIconWrapper>
                  <Chevron strokeColor={colors.mediumDarkGray} />
                </PromoIconWrapper>
              </Promo>
            </PromoWrapper>
          ))}
        </Slider>
      </SliderContainer>
    </Container>
  )
}

export default MobilePromoBanner
